import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraInput, SpecraOverlayDrawer } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { portfolioClient } from '../../../lib/connect/clients';
import { useLandscapeStrategyKey } from '../hooks/useLandscapeStrategy';
export function MarketObservationEditor({ landscapeId, onClose }) {
    const [title, setTitle] = useState('');
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [asOf, setAsOf] = useState('');
    const [provenance, setProvenance] = useState('');
    const cache = useQueryClient();
    const key = useLandscapeStrategyKey(landscapeId);
    const busy = useRef(false);
    const valid = !!title.trim() && !!provenance.trim() && /^\d{4}-\d{2}-\d{2}$/.test(asOf) && /^[A-Z]{3}$/.test(currency) && (!amount || /^\d+(\.\d{1,2})?$/.test(amount));
    const mutation = useMutation({
        mutationFn: ()=>portfolioClient.addMarketObservation({
                landscapeId,
                title: title.trim(),
                amount: amount || undefined,
                currency,
                asOf,
                provenance: provenance.trim()
            }),
        onSuccess: async ()=>{
            await cache.invalidateQueries({
                queryKey: key
            });
            onClose();
        },
        onSettled: ()=>{
            busy.current = false;
        }
    });
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: true,
        onOpenChange: (open)=>{
            if (!open && !mutation.isPending) onClose();
        },
        title: "Record market observation",
        description: "A recorded estimate, potentially overlapping other observations. Leave amount blank if unknown.",
        children: /*#__PURE__*/ _jsxs("form", {
            className: "space-y-3",
            onSubmit: (e)=>{
                e.preventDefault();
                if (!valid || busy.current) return;
                busy.current = true;
                mutation.mutate();
            },
            children: [
                /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Title",
                    placeholder: "Title",
                    "data-testid": Testids.landscapeStrategy.marketTitle,
                    value: title,
                    onChange: (e)=>setTitle(e.target.value),
                    required: true
                }),
                /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Amount (optional)",
                    placeholder: "Amount (optional)",
                    "data-testid": Testids.landscapeStrategy.marketAmount,
                    value: amount,
                    onChange: (e)=>setAmount(e.target.value),
                    inputMode: "decimal"
                }),
                /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Currency",
                    value: currency,
                    onChange: (e)=>setCurrency(e.target.value.toUpperCase()),
                    maxLength: 3
                }),
                /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "As-of date",
                    type: "date",
                    "data-testid": Testids.landscapeStrategy.marketDate,
                    value: asOf,
                    onChange: (e)=>setAsOf(e.target.value),
                    required: true
                }),
                /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Provenance",
                    placeholder: "Provenance",
                    "data-testid": Testids.landscapeStrategy.marketProvenance,
                    value: provenance,
                    onChange: (e)=>setProvenance(e.target.value),
                    required: true
                }),
                !valid && /*#__PURE__*/ _jsx("p", {
                    "aria-live": "polite",
                    children: "Enter title, provenance, as-of date and a three-letter currency. Amount is optional; use a nonnegative number with up to two decimal places."
                }),
                mutation.error && /*#__PURE__*/ _jsx("p", {
                    role: "alert",
                    children: "Could not record this observation. Your entries are retained; retry saving."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    type: "submit",
                    "data-testid": Testids.landscapeStrategy.marketSave,
                    disabled: !valid || mutation.isPending,
                    isLoading: mutation.isPending,
                    children: "Save observation"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    type: "button",
                    variant: "ghost",
                    disabled: mutation.isPending,
                    onClick: onClose,
                    children: "Cancel"
                })
            ]
        })
    });
}
